import { Payment } from './payment';

// Define Firestore Timestamp interface for handling Firestore timestamp objects
interface FirestoreTimestamp {
    seconds: number;
    nanoseconds: number;
    toDate?: () => Date;
}

/** How an incoming bank transfer was matched to this planned payment. */
export interface PlannedPaymentMatch {
    transactionId: string;
    matchedAt: Date;
    source: 'structured_comm' | 'iban_amount' | 'manual' | 'fuzzy';
    confidence: number;
}

export class PlannedPayment extends Payment {
    paid!: boolean;
    /** When the payment was actually settled (empty while unpaid). */
    paidAt?: Date;
    skipped!: boolean;
    partialPayment?: number;
    realIndexApplied?: boolean;
    overdue?: boolean;
    terminated!: boolean;
    /** Belgian structured communication embedded in the payment request (+++/+++/+++). */
    paymentReference?: string;
    /** Set when an open-banking / imported transfer was reconciled to this payment. */
    match?: PlannedPaymentMatch;
    // paymentUID and isIncome are inherited from Payment base class

    constructor(
        paymentDate: Date | FirestoreTimestamp,
        amount: number,
        description = '',
        paid = false,
        skipped = false,
        partialPayment?: number,
        realIndexApplied?: boolean,
        overdue?: boolean,
        terminated = false,
        paymentUID = '',
        isIncome = true,
        currency = 'EUR'
    ) {
        // Handle Firestore timestamp by converting to date
        const date = PlannedPayment.convertToDate(paymentDate);
        // Call parent constructor with proper parameters
        super(date, amount, currency, isIncome, description);
        this.paymentUID = paymentUID; // Set the inherited field
        this.paid = paid;
        this.skipped = skipped;
        this.partialPayment = partialPayment;
        this.realIndexApplied = realIndexApplied;
        this.overdue = overdue;
        this.terminated = terminated;
    }

    /**
     * Helper method to convert Firestore timestamps or other date formats to Date objects
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static override convertToDate(date: Date | FirestoreTimestamp | any): Date {
        if (!date) return new Date();
        
        // Handle Firestore timestamp objects
        if (typeof date === 'object' && 'seconds' in date && 'nanoseconds' in date) {
            return new Date(date.seconds * 1000 + date.nanoseconds / 1000000);
        }
        
        // Use toDate() method if available (Firestore timestamp)
        if (typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
            return date.toDate();
        }
        
        // Already a Date
        if (date instanceof Date) {
            return date;
        }
        
        // String date
        if (typeof date === 'string') {
            return new Date(date);
        }
        
        // Default to current date if can't parse
        try {
            return new Date(date);
        } catch (error) {
            console.error('Error converting date in PlannedPayment:', error);
            return new Date();
        }
    }
}